import React, { useEffect, useState } from "react";
import { enrollBatch, listCompanyContracts, listMyBatches } from "../../../api/company";
import { formatDate } from "../../../utils/date";

export default function EnrollBatchPage() {
	const [contracts, setContracts] = useState([]);
	const [batches, setBatches] = useState([]);
	const [loadingContracts, setLoadingContracts] = useState(true);
	const [loadingBatches, setLoadingBatches] = useState(true);
	const [batchId, setBatchId] = useState("");
	const [contractId, setContractId] = useState("");
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState(null);
	const [result, setResult] = useState(null);
	const [fieldErrors, setFieldErrors] = useState({});

	useEffect(() => {
		(async () => {
			setLoadingContracts(true);
			setLoadingBatches(true);
			try {
				const [contractsData, batchesData] = await Promise.all([
					listCompanyContracts(),
					listMyBatches(),
				]);
				setContracts(Array.isArray(contractsData) ? contractsData : []);
				setBatches(Array.isArray(batchesData) ? batchesData : []);
			} catch {
				setContracts([]);
				setBatches([]);
			} finally {
				setLoadingContracts(false);
				setLoadingBatches(false);
			}
		})();
	}, []);

	const onSubmit = async (e) => {
		e.preventDefault();
		setError(null);
		setResult(null);
		const b = Number(batchId);
		const c = Number(contractId);
		const err = {};
		if (!b) err.batchId = "Selecciona un lote.";
		if (!c) err.contractId = "Selecciona un contrato/curso.";
		setFieldErrors(err);
		if (Object.keys(err).length > 0) return;

		setLoading(true);
		try {
			const data = await enrollBatch({ batch_id: b, company_course_id: c });
			setResult(data);
			setBatchId("");
			setContractId("");
			setFieldErrors({});
		} catch (e2) {
			const msg =
				e2?.response?.data?.error ||
				e2?.response?.data?.detail ||
				"No se pudo realizar la matrícula. Revisa que el lote y el contrato sean válidos.";
			setError(typeof msg === "string" ? msg : JSON.stringify(msg));
		} finally {
			setLoading(false);
		}
	};

	return (
		<>
			<div className="p-4 backoffice-tables flex-grow-1 overflow-auto font-n">

				<div>
					<h2 className="portal-emp-title">Matrícula masiva</h2>

					<section className="card card-table">
						<div className="card-body">
							<h5 className="mb-3">Matricular grupo en un curso</h5>
							<form onSubmit={onSubmit}>
								<div className="mb-3">
									<label htmlFor="batch-id" className="form-label">Lote (carga de estudiantes)</label>
									<select
										id="batch-id"
										className={`form-select ${fieldErrors.batchId ? "is-invalid" : ""}`}
										value={batchId}
										onChange={(e) => { setBatchId(e.target.value); if (fieldErrors.batchId) setFieldErrors((p) => ({ ...p, batchId: undefined })); }}
										disabled={loadingBatches}
										aria-invalid={!!fieldErrors.batchId}
									>
										<option value="">Selecciona un lote</option>
										{batches.map((batch) => (
											<option key={batch.id} value={batch.id}>
												ID {batch.id} — {batch.file_name} ({formatDate(batch.created_at) || batch.created_at})
											</option>
										))}
									</select>
									{fieldErrors.batchId && <div className="invalid-feedback">{fieldErrors.batchId}</div>}
									{!loadingBatches && batches.length === 0 && (
										<small className="text-muted">No tienes lotes. Sube un Excel en Carga de estudiantes.</small>
									)}
								</div>

								<div className="mb-4">
									<label htmlFor="contract-id" className="form-label">Contrato (curso + periodo de vigencia)</label>
									<select
										id="contract-id"
										className={`form-select ${fieldErrors.contractId ? "is-invalid" : ""}`}
										value={contractId}
										onChange={(e) => { setContractId(e.target.value); if (fieldErrors.contractId) setFieldErrors((p) => ({ ...p, contractId: undefined })); }}
										disabled={loadingContracts}
										aria-invalid={!!fieldErrors.contractId}
									>
										<option value="">Selecciona un contrato</option>
										{contracts.map((c) => (
											<option key={c.id} value={c.id} disabled={!c.can_enroll}>
												{c.course_code || ""} {c.course_name} — vigencia {c.contract_start_date} a {c.contract_end_date}
												{c.period_start && c.period_end ? ` (periodo ${c.period_start}–${c.period_end})` : ""}
												{c.can_enroll
													? c.in_force
														? ` · ${c.remaining_slots} cupos`
														: ` · ${c.remaining_slots} cupos (preinscripcion habilitada)`
													: " — no disponible"}
											</option>
										))}
									</select>
									{fieldErrors.contractId && <div className="invalid-feedback">{fieldErrors.contractId}</div>}
									{!loadingContracts && contracts.length === 0 && (
										<small className="text-muted">No hay contratos. Administración debe asignar cursos y cupos a tu organización.</small>
									)}
								</div>

								<button className="admin-btn" type="submit" disabled={loading || !batchId || !contractId}>
									{loading ? "Procesando…" : (
										<>
											<i className="ri-user-add-line me-1" aria-hidden />
											<span>Matricular estudiantes</span>
										</>
									)}
								</button>
							</form>
						</div>
					</section>

					{error && (
						<div className="alert alert-danger portal-emp-alert mt-3" role="alert">
							<i className="ri-error-warning-line me-2" aria-hidden />
							{error}
						</div>
					)}

					{result && (
						<div className="alert alert-success portal-emp-alert portal-emp-result mt-3" role="alert">
							<h4 className="alert-heading">Matrícula completada</h4>
							<p className="mb-1">{result.message}</p>
							<p className="mb-0"><strong>Estudiantes matriculados:</strong> {result.students_enrolled}</p>
							{result.skipped_max_courses > 0 && (
								<p className="mb-0 mt-2"><strong>Omitidos (máx. 2 cursos por persona):</strong> {result.skipped_max_courses}</p>
							)}
						</div>
					)}
				</div>
			</div>
		</>
	);
}
